import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { PortalTarget } from '../../../store/projectStore';
import type { WorkspaceSummary } from '../../../electron-shared/workspace/workspace-descriptor';
import type { SurfaceIconComponent } from '../../layout/target-selector/attackSurfaces';
import type { AttackSurfaceGroup } from '../../layout/target-selector/useAttackSurfaceGroups';
import { compareByName } from '../../layout/target-selector/compareByName';

export const ALL_SURFACES_FILTER = 'all';

export type LauncherSurfaceFilter = typeof ALL_SURFACES_FILTER | TargetTypeType;

export type LauncherTargetItem = {
  target: PortalTarget;
  icon: SurfaceIconComponent;
  recent: WorkspaceSummary | null;
};

const compareLauncherItems = (
  first: LauncherTargetItem,
  second: LauncherTargetItem,
): number => {
  if (first.recent && second.recent) {
    return second.recent.lastOpenedAt - first.recent.lastOpenedAt;
  }

  if (first.recent || second.recent) {
    return first.recent ? -1 : 1;
  }

  return compareByName(first.target, second.target);
};

const indexLatestRecentsByTargetId = ({
  recents,
  tenantId,
}: {
  recents: readonly WorkspaceSummary[];
  tenantId: string | null;
}): ReadonlyMap<string, WorkspaceSummary> =>
  recents
    .filter((recent) => recent.tenantId === tenantId)
    .reduce((latestByTargetId, recent) => {
      const latest = latestByTargetId.get(recent.targetId);

      return !latest || recent.lastOpenedAt > latest.lastOpenedAt
        ? latestByTargetId.set(recent.targetId, recent)
        : latestByTargetId;
    }, new Map<string, WorkspaceSummary>());

export function buildLauncherTargetItems({
  groups,
  query,
  surfaceFilter,
  recents,
  tenantId,
}: {
  groups: readonly AttackSurfaceGroup[];
  query: string;
  surfaceFilter: LauncherSurfaceFilter;
  recents: readonly WorkspaceSummary[];
  tenantId: string | null;
}): LauncherTargetItem[] {
  const normalizedQuery = query.trim().toLowerCase();
  const latestRecentByTargetId = indexLatestRecentsByTargetId({
    recents,
    tenantId,
  });

  return groups
    .filter(
      ({ surface }) =>
        surfaceFilter === ALL_SURFACES_FILTER ||
        surface.targetType === surfaceFilter,
    )
    .flatMap(({ surface, targets }) =>
      targets
        .filter((target) => target.name.toLowerCase().includes(normalizedQuery))
        .map((target) => ({
          target,
          icon: surface.icon,
          recent: latestRecentByTargetId.get(target.id) ?? null,
        })),
    )
    .sort(compareLauncherItems);
}

export type HighlightSegment = { text: string; isMatch: boolean };

export function splitHighlightSegments({
  text,
  query,
}: {
  text: string;
  query: string;
}): HighlightSegment[] {
  const normalizedQuery = query.trim().toLowerCase();
  const matchIndex = normalizedQuery
    ? text.toLowerCase().indexOf(normalizedQuery)
    : -1;

  if (matchIndex < 0) {
    return [{ text, isMatch: false }];
  }

  const matchEnd = matchIndex + normalizedQuery.length;

  return [
    { text: text.slice(0, matchIndex), isMatch: false },
    { text: text.slice(matchIndex, matchEnd), isMatch: true },
    { text: text.slice(matchEnd), isMatch: false },
  ].filter((segment) => segment.text.length > 0);
}
